import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createPasswordResetRequest } from '../services/api';
import toast from 'react-hot-toast';
import '../styles/Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUsername, setRememberUsername] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Şifre sıfırlama state'leri
  const [showResetOptions, setShowResetOptions] = useState(false);
  const [resetMethod, setResetMethod] = useState(null);
  const [resetUsername, setResetUsername] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberUsername = localStorage.getItem('rememberUsername') === 'true';
    
    if (savedRememberUsername && savedUsername) {
      setUsername(savedUsername);
      setRememberUsername(true);
    }
    
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedRememberMe) {
      setRememberMe(true);
    }
  }, []);

 
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(username, password, rememberMe);
      
      if (rememberUsername) {
        localStorage.setItem('rememberedUsername', username);
        localStorage.setItem('rememberUsername', 'true');
      } else {
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberUsername');
      }
      
      toast.success('Giriş başarılı! Hoş geldiniz 👋', {
        duration: 3000,
        style: {
          background: '#4caf50',
          color: '#fff',
          fontWeight: '500',
          padding: '16px 24px',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#4caf50',
        },
      });
      
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 900);
      
    } catch (err) {
      let errorMsg = 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';
      
      if (err.message) {
        if (err.message.includes('Invalid credentials') || err.message.includes('Kullanıcı adı veya şifre')) {
          errorMsg = 'Kullanıcı adı veya şifre hatalı!';
        } else if (err.message.includes('inactive') || err.message.includes('devre dışı')) {
          errorMsg = 'Bu hesap devre dışı bırakılmış. Yönetici ile iletişime geçin.';
        } else {
          errorMsg = err.message;
        }
      }

      toast.error(errorMsg, {
        duration: 5000,
        style: {
          background: '#d32f2f',
          color: '#fff',
          fontWeight: '500',
          padding: '16px 24px',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
          maxWidth: '400px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#d32f2f',
        },
      });
      
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Admin'e talep gönder
  const handleAdminResetRequest = async (e) => {
    e.preventDefault();
    
    if (!resetUsername.trim()) {
      toast.error('Lütfen kullanıcı adınızı girin');
      return;
    }

    setResetLoading(true);
    
    try {
      await createPasswordResetRequest({ username: resetUsername.trim() });
      
      toast.success(
        'Şifre sıfırlama talebi gönderildi! Yöneticiniz en kısa sürede işlem yapacaktır.',
        { duration: 5000 }
      );
      handleCloseAllModals();
    } catch (err) {
      toast.success(
        'Şifre sıfırlama talebi gönderildi! Yöneticiniz en kısa sürede işlem yapacaktır.',
        { duration: 5000 }
      );
      handleCloseAllModals();
    } finally {
      setResetLoading(false);
    }
  };

  // Email ile sıfırlama
  const handleEmailResetRequest = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast.error('Lütfen email adresinizi girin');
      return;
    }

    setResetLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/password-reset/email/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Şifre sıfırlama linki email adresinize gönderildi!', { duration: 5000 });
        handleCloseAllModals();
      } else {
        toast.error(data.detail || 'Bir hata oluştu');
      }
    } catch (err) {
      toast.error('Sunucuya bağlanılamadı');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseAllModals = () => {
    if (!resetLoading) {
      setShowResetOptions(false);
      setResetMethod(null);
      setResetUsername('');
      setResetEmail('');
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Ekol Tabela ERP</h1>
          <p>Hoş Geldiniz</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label>Kullanıcı Adı</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Kullanıcı adınızı girin"
              required
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label>Şifre</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifrenizi girin"
                required
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                disabled={loading}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="remember-options" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label className="checkbox-label" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '12px', 
                  border: '1px solid #ddd', 
                  borderRadius: '8px' 
              }}>
                  <input
                      type="checkbox"
                      checked={rememberUsername}
                      style={{ 
                          margin: 0, 
                          marginTop: '2px'
                      }}
                      onChange={(e) => setRememberUsername(e.target.checked)}
                  />
                  <span>Kullanıcı adımı hatırla</span>
              </label>
              
              <label className="checkbox-label" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '12px', 
                  border: '1px solid #ddd', 
                  borderRadius: '8px' 
              }}>
                  <input
                      type="checkbox"
                      checked={rememberMe}
                      style={{ 
                          margin: 0, 
                          marginTop: '2px'
                      }}
                      onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Beni hatırla (7 gün)</span>
              </label>
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>

        <div className="login-footer">
          <button 
            type="button" 
            className="forgot-password-btn"
            onClick={() => setShowResetOptions(true)}
            disabled={loading}
          >
            Şifremi Unuttum
          </button>
        </div>
      </div>
      
      {/* Şifre Sıfırlama Seçenekleri Modal */}
      {showResetOptions && !resetMethod && (
        <div className="modal-overlay" onClick={handleCloseAllModals}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '420px'}}>
            <div className="modal-header">
              <h3>Şifre Sıfırlama</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>×</button>
            </div>
            
            <div className="modal-body">
              <p style={{color: '#666', textAlign: 'center', marginBottom: '25px'}}>
                Nasıl sıfırlamak istersiniz?
              </p>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                <button
                  onClick={() => setResetMethod('email')}
                  style={{
                    padding: '18px 20px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    textAlign: 'left',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={e => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseOut={e => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <span style={{fontSize: '20px', marginRight: '10px'}}>📧</span>
                  Email ile Sıfırla
                  <div style={{fontSize: '12px', opacity: 0.85, marginTop: '5px', fontWeight: '400'}}>
                    Kayıtlı email adresinize anında link gönderilir
                  </div>
                </button>
                
                <button
                  onClick={() => setResetMethod('admin')}
                  style={{
                    padding: '18px 20px',
                    background: '#f8f9fa',
                    color: '#333',
                    border: '2px solid #e0e0e0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.background = '#f0f4ff';
                  }}
                  onMouseOut={e => {
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.background = '#f8f9fa';
                  }}
                >
                  <span style={{fontSize: '20px', marginRight: '10px'}}>👤</span>
                  Yöneticiye Talep Gönder
                  <div style={{fontSize: '12px', color: '#888', marginTop: '5px', fontWeight: '400'}}>
                    Yönetici onayladıktan sonra şifreniz sıfırlanır
                  </div>
                </button>
              </div>
              
              <button
                onClick={handleCloseAllModals}
                style={{
                  width: '100%',
                  marginTop: '20px',
                  padding: '12px',
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email ile Sıfırlama Formu */}
      {showResetOptions && resetMethod === 'email' && (
        <div className="modal-overlay" onClick={handleCloseAllModals}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '420px'}}>
            <div className="modal-header">
              <h3>📧 Email ile Şifre Sıfırla</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>×</button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleEmailResetRequest}>
                <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>
                  Hesabınıza kayıtlı email adresini girin, size şifre sıfırlama linki gönderelim.
                </p>
                
                <div className="form-group">
                  <label>Email Adresi</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    required
                    disabled={resetLoading}
                    autoFocus
                  />
                </div>

                <div className="form-actions" style={{marginTop: '20px', display: 'flex', gap: '10px'}}>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setResetMethod(null)}
                    disabled={resetLoading}
                    style={{flex: 1}}
                  >
                    ← Geri
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={resetLoading}
                    style={{flex: 2}}
                  >
                    {resetLoading ? 'Gönderiliyor...' : 'Link Gönder'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Admin'e Talep Formu */}
      {showResetOptions && resetMethod === 'admin' && (
        <div className="modal-overlay" onClick={handleCloseAllModals}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '420px'}}>
            <div className="modal-header">
              <h3>👤 Yöneticiye Talep Gönder</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>×</button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleAdminResetRequest}>
                <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>
                  Kullanıcı adınızı girin, yöneticinize şifre sıfırlama talebi gönderilecektir.
                </p>
                
                <div className="form-group">
                  <label>Kullanıcı Adı</label>
                  <input
                    type="text"
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    placeholder="Kullanıcı adınız"
                    required
                    disabled={resetLoading}
                    autoFocus
                  />
                </div>

                <div className="form-actions" style={{marginTop: '20px', display: 'flex', gap: '10px'}}>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setResetMethod(null)}
                    disabled={resetLoading}
                    style={{flex: 1}}
                  >
                    ← Geri
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={resetLoading}
                    style={{flex: 2}}
                  >
                    {resetLoading ? 'Gönderiliyor...' : 'Talep Gönder'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Login;